import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Trust customer-logo strip + uptime SLA badge — Blueprint Decision #44.
 * Public, no-auth surface used on /trust + /pricing.
 *
 * - Reads `GET /api/trust/customer-logos` (published rows).
 * - Reads `GET /api/trust/uptime-sla` (computed 30-day uptime from incidents).
 * - Renders nothing if both endpoints return empty / fail (defensive).
 */
export function TrustCustomerLogosStrip() {
  const [logos, setLogos] = useState([]);
  const [sla, setSla] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`${API}/trust/customer-logos`).catch(() => ({ data: { logos: [] } })),
          axios.get(`${API}/trust/uptime-sla`).catch(() => ({ data: null })),
        ]);
        if (!mounted) return;
        setLogos(a.data?.logos || []);
        setSla(b.data || null);
      } catch (e) { /* silent */ }
    })();
    return () => { mounted = false; };
  }, []);

  if (logos.length === 0 && !sla) return null;

  return (
    <section className="cn-section py-10 bg-cn-bone-50 border-y border-cn-border" data-testid="trust-customer-logos-strip">
      <div className="cn-container max-w-5xl">
        {sla && (
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6" data-testid="trust-uptime-badge">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-semibold">
              <Activity className="w-4 h-4" />
              {sla.uptime_30d_pct?.toFixed(2)}% uptime · last 30 days
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cn-primary/10 text-cn-primary border border-cn-primary/20 text-sm font-semibold">
              <ShieldCheck className="w-4 h-4" />
              Enterprise SLA target {sla.sla_tiers?.enterprise?.target}%
            </span>
            {!sla.meeting_sla_30d && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold">
                Below growth-tier target this window
              </span>
            )}
          </div>
        )}
        {logos.length > 0 && (
          <>
            <p className="text-center text-[11px] uppercase tracking-[0.2em] font-bold text-cn-text-muted mb-5" data-testid="trust-customer-logos-eyebrow">
              Trusted across the post-acute care ecosystem
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 items-center" data-testid="trust-customer-logos-grid">
              {logos.slice(0, 12).map((l) => (
                <div key={l.id} className="flex items-center justify-center px-3" data-testid={`trust-logo-${l.id}`}>
                  {l.logo_url ? (
                    <img
                      src={l.logo_url}
                      alt={l.name}
                      className="h-10 max-w-[140px] object-contain opacity-70 hover:opacity-100 transition-opacity"
                      loading="lazy"
                    />
                  ) : (
                    <span className="font-display font-bold text-base text-cn-text/70">{l.name}</span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
